// @flow
import React from 'react';
import styled from '@emotion/styled';
import { FixedSizeList as List } from 'react-window';
import { isNumber } from 'lodash';
import AutoSizer from 'react-virtualized-auto-sizer';

import OpenEntityRow from './OpenEntityRow';
import TableHead from './TableHead';

type Props = {
  players: Array<Object>,
  sportHasTeam: boolean,
  useAverage?: boolean,
  maxExposurePerPlayer: number,
  onAdd: (data: Object) => void,
  onRemove: (id: string) => void,
  onSelectSettingsTab: (tab: string) => void,
  onToggleEntityProperty: (id: string, field: string) => void,
  onUpdateEntityProperty: (id: string, field: string, value: any) => void,
  isCaptainFilter: boolean
};

type RowProps = {
  index: number,
  style: Object
};

const columns = [
  {
    field: 'name',
    label: 'Name'
  },
  {
    field: 'positionAbbr',
    label: 'POS',
    width: 55,
    largeScreenWidth: 70,
    narrow: true
  },
  {
    field: 'teamName',
    label: 'Team',
    width: 40,
    largeScreenWidth: 50,
    narrow: true,
    tabletLandscapeAndUp: true
  },
  {
    field: 'salary',
    label: 'Salary',
    width: 50,
    largeScreenWidth: 70,
    narrow: true
  },
  {
    field: 'insiderGames',
    label: 'GP',
    width: 40,
    largeScreenWidth: 50,
    narrow: true,
    tabletLandscapeAndUp: true
  },
  {
    field: 'insiderOpponent',
    label: 'OPP',
    width: 40,
    largeScreenWidth: 50,
    narrow: true,
    tabletLandscapeAndUp: true
  },
  {
    field: 'insiderSalaryScoreRatio',
    label: '$/PT',
    width: 40,
    largeScreenWidth: 50,
    narrow: true,
    tabletLandscapeAndUp: true
  },
  {
    field: 'insiderForm',
    label: 'L5',
    width: 40,
    largeScreenWidth: 50,
    narrow: true,
    tabletLandscapeAndUp: true
  },
  {
    field: 'insiderScoreUpdated',
    label: 'PROJ',
    width: 150,
    largeScreenWidth: 150,
    narrow: true
  }
];

const columnsWithoutTeam = [
  {
    field: 'name',
    label: 'Name'
  },
  {
    field: 'positionAbbr',
    label: 'POS',
    width: 75,
    largeScreenWidth: 95,
    narrow: true
  },
  {
    field: 'salary',
    label: 'Salary',
    width: 70,
    largeScreenWidth: 95,
    narrow: true
  },
  {
    field: 'insiderSalaryScoreRatio',
    label: '$/PT',
    width: 80,
    largeScreenWidth: 100,
    narrow: true,
    tabletLandscapeAndUp: true
  },
  {
    field: 'insiderForm',
    label: 'Form',
    width: 80,
    largeScreenWidth: 100,
    narrow: true,
    tabletLandscapeAndUp: true
  },
  {
    field: 'insiderScoreUpdated',
    label: 'PROJ',
    width: 150,
    largeScreenWidth: 150,
    narrow: true
  }
];

const DEFAULT_ITEM_COUNT = 40;
const ROW_HEIGHT = 40;

const Wrapper = styled.div({
  flex: '1 1 auto',
  backgroundColor: 'white'
});

const PlayerSelectionTable = ({
  players,
  onAdd,
  onRemove,
  sportHasTeam,
  useAverage,
  maxExposurePerPlayer,
  onUpdateEntityProperty,
  onSelectSettingsTab,
  onToggleEntityProperty,
  isCaptainFilter
}: Props) => {
  if (players.length === 0) {
    return null;
  }

  const height = ROW_HEIGHT * Math.min(players.length, DEFAULT_ITEM_COUNT);

  const Row = ({ index, style }: RowProps) => {
    const player = players[index];

    return (
      <OpenEntityRow
        id={player.id}
        key={player.idStr}
        player={player}
        sportHasTeam={sportHasTeam}
        onSelect={onAdd}
        onRemove={onRemove}
        showExposureWarning={
          isNumber(player.insiderExposure)
            ? player.insiderExposure < 100
            : maxExposurePerPlayer < 100
        }
        onSelectSettingsTab={onSelectSettingsTab}
        onToggleEntityProperty={onToggleEntityProperty}
        onUpdateEntityProperty={onUpdateEntityProperty}
        style={style}
        isCaptain={isCaptainFilter}
      />
    );
  };

  const projectionLabel = useAverage ? 'AVG' : 'PROJ';
  const tableColumns = (sportHasTeam ? columns : columnsWithoutTeam).map(column =>
    column.field === 'insiderScoreUpdated' ? { ...column, label: projectionLabel } : column
  );

  return (
    <div>
      <TableHead columns={tableColumns} hasScrollbar={players.length > DEFAULT_ITEM_COUNT} />
      <Wrapper>
        <AutoSizer disableHeight>
          {({ width }) => (
            <List height={height} itemCount={players.length} itemSize={ROW_HEIGHT} width={width}>
              {Row}
            </List>
          )}
        </AutoSizer>
      </Wrapper>
    </div>
  );
};

export default React.memo(PlayerSelectionTable);
